import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@/components/common';
import { todayCalendarDate } from '@/lib/calendar-date';
import { useCurrencies } from '@/hooks/useCurrencies';
import { NewConversionFormSchema, RATE_DIRECTION, type NewConversionForm } from './types';
import { OperationField, fieldBorderClass, operationInputClass } from './OperationField';
import { useManualFxQuote } from './useManualFxQuote';
import { ConversionRecap } from './ConversionRecap';

export interface ConversionOperationFormProps {
  clientId: number | undefined;
  onSubmit: (data: NewConversionForm) => void;
  onCancel: () => void;
  isSubmitting: boolean;
}

export function ConversionOperationForm({
  clientId,
  onSubmit,
  onCancel,
  isSubmitting,
}: ConversionOperationFormProps) {
  const {
    codes: currencyCodes,
    unavailable: currenciesUnavailable,
    error: currencyError,
  } = useCurrencies();
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isSubmitting: isValidating },
  } = useForm<NewConversionForm>({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Zod 3/4 type mismatch with @hookform/resolvers
    resolver: zodResolver(NewConversionFormSchema as any),
    mode: 'onBlur',
    defaultValues: {
      valueDate: todayCalendarDate(),
      sellAmount: '',
      sellCurrencyCode: '',
      buyCurrencyCode: '',
      rateDirection: RATE_DIRECTION.SELL_TO_BUY,
      lpRate: '',
      clientRate: '',
      marginCurrencyCode: '',
      description: '',
    },
  });

  useEffect(() => {
    setValue('clientId', clientId as number, { shouldValidate: false });
  }, [clientId, setValue]);

  const sellAmount = watch('sellAmount') ?? '';
  const sellCurrencyCode = watch('sellCurrencyCode');
  const buyCurrencyCode = watch('buyCurrencyCode');
  const rateDirection = watch('rateDirection');
  const lpRate = watch('lpRate') ?? '';
  const clientRate = watch('clientRate') ?? '';
  const marginCurrencyCode = watch('marginCurrencyCode');

  useEffect(() => {
    const sell = currencyCodes.includes(sellCurrencyCode)
      ? sellCurrencyCode
      : (currencyCodes[0] ?? '');
    if (sell !== sellCurrencyCode) setValue('sellCurrencyCode', sell);
    if (!currencyCodes.includes(buyCurrencyCode)) {
      setValue('buyCurrencyCode', currencyCodes.find((code) => code !== sell) ?? '');
    }
  }, [currencyCodes, sellCurrencyCode, buyCurrencyCode, setValue]);

  // The margin is denominated in one of the two legs. When the operator
  // changes a currency, a stale third one would be refused by the server.
  useEffect(() => {
    if (marginCurrencyCode !== sellCurrencyCode && marginCurrencyCode !== buyCurrencyCode) {
      setValue('marginCurrencyCode', sellCurrencyCode);
    }
  }, [marginCurrencyCode, sellCurrencyCode, buyCurrencyCode, setValue]);

  const { quote, isQuoting, error } = useManualFxQuote({
    sellAmount,
    sellCurrencyCode,
    buyCurrencyCode,
    rateDirection,
    lpRate,
    clientRate,
    marginCurrencyCode,
  });

  // Un cours client au-dessus du cours LP est une faute de frappe
  // ordinaire : la maison paierait la conversion. Le serveur le refuse,
  // mais autant le dire ici plutot que de laisser confirmer une operation
  // qui sera rejetee.
  const marginIsNegative = quote !== null && quote.margin.startsWith('-');

  return (
    <form
      className="mt-6"
      onSubmit={handleSubmit((data) => {
        if (
          !currenciesUnavailable &&
          currencyCodes.includes(data.sellCurrencyCode) &&
          currencyCodes.includes(data.buyCurrencyCode)
        )
          onSubmit(data);
      })}
      data-testid="conversion-operation-form"
    >
      {currencyError && (
        <p role="alert" className="mb-4 text-sm text-red-700">
          Unable to load currencies. Please reload and try again.
        </p>
      )}
      <h4 className="mb-4 text-sm font-semibold text-[#333]">Internal FX conversion</h4>

      <div className="max-w-xs">
        <OperationField id="fx-date" label="Date" required error={errors.valueDate?.message}>
          <input
            id="fx-date"
            type="date"
            {...register('valueDate')}
            className={fieldBorderClass(Boolean(errors.valueDate))}
            data-testid="conversion-date"
          />
        </OperationField>
      </div>

      <div className="mt-5">
        <OperationField
          id="fx-sell-amount"
          label="Operation"
          required
          error={errors.sellAmount?.message}
        >
          <div className="flex">
            <span className="flex items-center rounded-l-lg border border-r-0 border-[#e0e0e0] bg-[#fafafa] px-4 py-2.5 text-sm font-semibold text-[#6a1b9a]">
              SELL
            </span>
            <input
              id="fx-sell-amount"
              type="text"
              inputMode="decimal"
              placeholder="0.00"
              {...register('sellAmount')}
              className={`${operationInputClass} rounded-none border-x-0 text-right ${
                errors.sellAmount ? 'border-[#c62828]' : 'border-[#e0e0e0]'
              }`}
              data-testid="conversion-sell-amount"
            />
            <select
              aria-label="Currency sold"
              {...register('sellCurrencyCode')}
              disabled={currenciesUnavailable}
              className={`${operationInputClass} w-auto rounded-l-none border-[#e0e0e0]`}
              data-testid="conversion-sell-currency"
            >
              <option value="" disabled>
                {currenciesUnavailable ? 'Currencies unavailable' : 'Select currency'}
              </option>
              {currencyCodes.map((code) => (
                <option key={code} value={code}>
                  {code}
                </option>
              ))}
            </select>
          </div>
        </OperationField>
      </div>

      <div className="mt-5">
        <OperationField id="fx-direction" label="Rate direction">
          <select
            id="fx-direction"
            {...register('rateDirection')}
            className={fieldBorderClass(false)}
            data-testid="conversion-rate-direction"
          >
            <option value={RATE_DIRECTION.SELL_TO_BUY}>
              1 {sellCurrencyCode} = rate {buyCurrencyCode}
            </option>
            <option value={RATE_DIRECTION.BUY_TO_SELL}>
              1 {buyCurrencyCode} = rate {sellCurrencyCode}
            </option>
          </select>
        </OperationField>
      </div>

      <div className="mt-5 grid grid-cols-1 gap-x-6 gap-y-5 sm:grid-cols-2">
        <OperationField
          id="fx-lp-rate"
          label="LP rate (Montelux)"
          required
          error={errors.lpRate?.message}
        >
          <input
            id="fx-lp-rate"
            type="text"
            inputMode="decimal"
            placeholder="0.00000000"
            {...register('lpRate')}
            className={fieldBorderClass(Boolean(errors.lpRate))}
            data-testid="conversion-lp-rate"
          />
        </OperationField>

        <OperationField
          id="fx-client-rate"
          label="Client rate"
          required
          error={errors.clientRate?.message}
        >
          <input
            id="fx-client-rate"
            type="text"
            inputMode="decimal"
            placeholder="0.00000000"
            {...register('clientRate')}
            className={fieldBorderClass(Boolean(errors.clientRate))}
            data-testid="conversion-client-rate"
          />
        </OperationField>
      </div>

      <div className="mt-5">
        <OperationField
          id="fx-result"
          label="Result (auto)"
          error={errors.buyCurrencyCode?.message}
        >
          <div className="flex">
            <span className="flex items-center rounded-l-lg border border-r-0 border-[#e0e0e0] bg-[#f5f5f5] px-4 py-2.5 text-sm font-semibold text-[#777]">
              BUY
            </span>
            <input
              id="fx-result"
              type="text"
              readOnly
              disabled
              value={quote?.buy_amount ?? ''}
              placeholder="0.00"
              className={`${operationInputClass} rounded-none border-x-0 border-[#e0e0e0] bg-[#f5f5f5] text-right`}
              data-testid="conversion-buy-amount"
            />
            <select
              aria-label="Currency bought"
              {...register('buyCurrencyCode')}
              disabled={currenciesUnavailable}
              className={`${operationInputClass} w-auto rounded-l-none border-[#e0e0e0]`}
              data-testid="conversion-buy-currency"
            >
              <option value="" disabled>
                {currenciesUnavailable ? 'Currencies unavailable' : 'Select currency'}
              </option>
              {currencyCodes.map((code) => (
                <option key={code} value={code}>
                  {code}
                </option>
              ))}
            </select>
          </div>
        </OperationField>
        {quote && (
          <p
            className="mt-1.5 text-xs font-semibold text-[#6a1b9a]"
            data-testid="conversion-rate-line"
          >
            1 {quote.sell_currency_code} = {quote.client_rate} {quote.buy_currency_code} (client
            rate)
          </p>
        )}
      </div>

      <div className="mt-5 grid grid-cols-1 gap-x-6 gap-y-5 sm:grid-cols-2">
        <OperationField id="fx-margin-currency" label="Margin — currency">
          <select
            id="fx-margin-currency"
            {...register('marginCurrencyCode')}
            disabled={currenciesUnavailable}
            className={fieldBorderClass(false)}
            data-testid="conversion-margin-currency"
          >
            <option value={sellCurrencyCode}>{sellCurrencyCode} (currency sold)</option>
            <option value={buyCurrencyCode}>{buyCurrencyCode} (currency bought)</option>
          </select>
        </OperationField>

        <OperationField id="fx-margin" label="Margin (profit)">
          <input
            id="fx-margin"
            type="text"
            readOnly
            tabIndex={-1}
            value={
              quote
                ? `${marginIsNegative ? '' : '+'}${quote.margin} ${quote.margin_currency_code}`
                : ''
            }
            className={`${operationInputClass} cursor-default border-[#e0e0e0] bg-[#f5f5f5] font-semibold ${
              marginIsNegative ? 'text-[#c62828]' : 'text-[#2e7d32]'
            }`}
            data-testid="conversion-margin"
          />
        </OperationField>
      </div>

      <div className="mt-5">
        <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-[#666]">
          Recap &amp; LP instruction
        </p>
        <ConversionRecap quote={quote} isQuoting={isQuoting} error={error} />
        {marginIsNegative && (
          <p
            className="mt-2 text-xs text-[#c62828]"
            role="alert"
            data-testid="conversion-margin-negative"
          >
            The client rate is better than the LP rate, so Montelux would pay for this conversion.
            Check both rates before booking.
          </p>
        )}
      </div>

      <div className="mt-5 grid grid-cols-1 sm:grid-cols-2">
        <OperationField id="fx-description" label="Description" error={errors.description?.message}>
          <input
            id="fx-description"
            type="text"
            maxLength={255}
            placeholder="optional"
            {...register('description')}
            className={fieldBorderClass(Boolean(errors.description))}
            data-testid="conversion-description"
          />
        </OperationField>
      </div>

      <div className="mt-6 flex items-center gap-3">
        <Button
          type="submit"
          disabled={
            currenciesUnavailable || isSubmitting || isValidating || !quote || marginIsNegative
          }
          data-testid="conversion-submit"
        >
          {isSubmitting ? 'Booking…' : 'Book operation'}
        </Button>
        <Button type="button" variant="outline" onClick={onCancel} data-testid="conversion-cancel">
          Cancel
        </Button>
      </div>
    </form>
  );
}
